'use client'
import { useEffect } from 'react';
import Image from 'next/image';

export default function ImageModal({ isOpen, imageSrc, onClose, alt = "Image" }) {
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen || !imageSrc) return null;

    return (
        <div
            className="image-modal-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
        >
            <button
                className="image-modal-close"
                onClick={onClose}
                aria-label="Close"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

            <div
                className="image-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="image-modal-wrapper">
                    <Image
                        src={imageSrc}
                        alt={alt}
                        width={1200}
                        height={800}
                        quality={90}
                        priority
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '90vh',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            </div>

            <style jsx>{`
                .image-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.95);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    cursor: zoom-out;
                    animation: fadeIn 0.2s ease-out;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                .image-modal-close {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    border-radius: 50%;
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: white;
                    transition: all 0.2s ease;
                    z-index: 10001;
                    backdrop-filter: blur(10px);
                }

                .image-modal-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                    transform: scale(1.1);
                }

                .image-modal-content {
                    max-width: 1400px;
                    width: 100%;
                    cursor: default;
                    animation: scaleIn 0.3s ease-out;
                }

                @keyframes scaleIn {
                    from {
                        transform: scale(0.9);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                .image-modal-wrapper {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                    background: #000;
                }

                @media (max-width: 768px) {
                    .image-modal-overlay {
                        padding: 10px;
                    }

                    .image-modal-close {
                        top: 10px;
                        right: 10px;
                        width: 40px;
                        height: 40px;
                    }
                }
            `}</style>
        </div>
    );
}
